import React, { useState, useEffect } from 'react';
import EvaluationLayout from '../../components/EvaluationLayout';
import {
  BarChart3,
  TrendingUp,
  Download,
  Filter,
  Calendar,
  Eye,
  RefreshCw,
  ArrowUpDown,
  Clock,
  Zap,
  DollarSign,
  Target,
  XCircle,
  ChevronDown,
  ChevronUp,
  FileText,

  Users
} from 'lucide-react';
import Button from '../../components/ui/button';
import Badge from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';
import { formatDate } from '../../lib/utils';
import {
  EvaluationReportDashboard,

  ComparativeAnalysis,
  ReportExporter
} from '../../components/reports';
import evaluationService from '../../services/evaluationService';

const ResultsVisualization = () => {
  const [results, setResults] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('accuracy');
  const [selectedModels, setSelectedModels] = useState(['all']);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [activeTab, setActiveTab] = useState('results');
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    performance: true,
    comparison: true,
    trends: true
  });

  // Initial load: fetch evaluations, then load results for the first one
  useEffect(() => {
    const loadEvaluations = async () => {
      try {
        setLoading(true);
        const resp = await evaluationService.getEvaluations({ per_page: 50 });
        const items = resp?.data?.data || resp?.data || [];
        setEvaluations(items);
        if (items.length && !selectedEvaluationId) {
          setSelectedEvaluationId(items[0].id);
        }
      } catch (e) {
        console.error('Failed to load evaluations:', e);
      } finally {
        setLoading(false);
      }
    };
    loadEvaluations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload results when selection or filters change
  useEffect(() => {
    loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeRange, selectedModels, selectedEvaluationId]);

  const loadResults = async () => {
    if (!selectedEvaluationId) {
      setResults([]);
      return;
    }
    try {
      setLoading(true);
      const { start_date, end_date } = computeDateRange(selectedTimeRange);
      const resp = await evaluationService.getEvaluationResults(selectedEvaluationId, {
        per_page: 100,
        start_date,
        end_date,
      });

      const paginator = resp?.data;
      const items = Array.isArray(paginator)
        ? paginator
        : (paginator?.data || []);

      const selectedEval = evaluations.find(e => e.id === selectedEvaluationId);
      const evaluationName = selectedEval?.name || 'Evaluation';
      const templateName = selectedEval?.template?.name || '';

      const mapped = items.map(r => ({
        id: r.id,
        evaluationId: r.evaluation_id,
        evaluationName,
        runId: r.run_id,
        timestamp: r.created_at,
        provider: r.provider,
        model: r.model,
        template: templateName,
        metrics: {
          // Optional metrics may be missing; default safely
          accuracy: r.metrics?.accuracy ?? null,
          responseTime: r.metrics?.response_time ?? null,
          tokenCount: {
            input: r.metrics?.token_count?.input ?? 0,
            output: r.metrics?.token_count?.output ?? 0,
            total: r.metrics?.token_count?.total ?? 0,
          },
          cost: r.metrics?.cost ?? null,
          qualityScore: r.metrics?.quality_score ?? null,
        },
        status: r.status,
      }));

      // Optional model filter on client side
      const filtered = selectedModels?.[0] && selectedModels[0] !== 'all'
        ? mapped.filter(m => m.model === selectedModels[0])
        : mapped;

      setResults(filtered);

      // Map results to reports format for ComparativeAnalysis and ReportExporter
      const mappedReports = filtered.map(r => ({
        id: r.id,
        generated_at: r.timestamp,
        overall_score: r.metrics.qualityScore ? r.metrics.qualityScore * 10 : 0,
        metrics: {
          accuracy: { score: r.metrics.accuracy || 0 },
          latency: { average_ms: r.metrics.responseTime ? r.metrics.responseTime * 1000 : 0 },
          security: { score: 85 }, // Placeholder as we don't have this metric in simple results
          offense: { content_safety_score: 90 }, // Placeholder
          hallucination: { factual_accuracy_score: r.metrics.accuracy || 0 } // Proxy with accuracy
        }
      }));
      setReports(mappedReports);

    } catch (error) {
      console.error('Failed to load results:', error);
      setResults([]);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const computeDateRange = (range) => {
    const end = new Date();
    const start = new Date();
    switch (range) {
      case '1d':
        start.setDate(end.getDate() - 1);
        break;
      case '7d':
        start.setDate(end.getDate() - 7);
        break;
      case '30d':
        start.setDate(end.getDate() - 30);
        break;
      case '90d':
        start.setDate(end.getDate() - 90);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }
    // Format as ISO strings compatible with backend filtering
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    };
  };

  const handleExportResults = async (format = 'csv') => {
    try {
      // TODO: Replace with actual API call
      // const response = await axios.get('/api/evaluations/results/export', {
      //   params: { format, timeRange: selectedTimeRange, models: selectedModels },
      //   responseType: 'blob'
      // });

      // Mock implementation
      const data = results.map(result => ({
        timestamp: result.timestamp,
        evaluation: result.evaluationName,
        provider: result.provider,
        model: result.model,
        accuracy: result.metrics.accuracy,
        responseTime: result.metrics.responseTime,
        cost: result.metrics.cost,
        qualityScore: result.metrics.qualityScore
      }));

      const csvContent = [
        Object.keys(data[0]).join(','),
        ...data.map(row => Object.values(row).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluation-results-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export results:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Prepare chart data
  const performanceOverTimeData = results.map(result => ({
    date: new Date(result.timestamp).toLocaleDateString(),
    accuracy: result.metrics.accuracy,
    responseTime: result.metrics.responseTime,
    cost: result.metrics.cost,
    qualityScore: result.metrics.qualityScore,
    model: result.model,
    provider: result.provider
  }));

  const modelComparisonData = results.reduce((acc, result) => {
    const key = `${result.provider}-${result.model}`;
    if (!acc[key]) {
      acc[key] = {
        name: `${result.provider} ${result.model}`,
        accuracy: [],
        responseTime: [],
        cost: [],
        qualityScore: []
      };
    }
    acc[key].accuracy.push(result.metrics.accuracy);
    acc[key].responseTime.push(result.metrics.responseTime);
    acc[key].cost.push(result.metrics.cost);
    acc[key].qualityScore.push(result.metrics.qualityScore);
    return acc;
  }, {});

  const modelComparisonChartData = Object.entries(modelComparisonData).map(([key, data]) => ({
    name: data.name,
    accuracy: data.accuracy.reduce((a, b) => a + b, 0) / data.accuracy.length,
    responseTime: data.responseTime.reduce((a, b) => a + b, 0) / data.responseTime.length,
    cost: data.cost.reduce((a, b) => a + b, 0) / data.cost.length,
    qualityScore: data.qualityScore.reduce((a, b) => a + b, 0) / data.qualityScore.length
  }));

  const costVsPerformanceData = results.map(result => ({
    cost: result.metrics.cost,
    accuracy: result.metrics.accuracy,
    model: `${result.provider} ${result.model}`,
    responseTime: result.metrics.responseTime
  }));



  const ResultDetailModal = ({ result, isOpen, onClose }) => {
    if (!isOpen || !result) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">
                Result Details - {result.evaluationName}
              </h2>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Run ID</p>
                <p className="font-medium">{result.runId}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Provider</p>
                <p className="font-medium">{result.provider}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Model</p>
                <p className="font-medium">{result.model}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Template</p>
                <p className="font-medium">{result.template}</p>
              </div>
            </div>

            {/* Metrics Grid */}
            <div>
              <h3 className="text-lg font-medium mb-4">Performance Metrics</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-secondary/50 rounded-lg">
                  <Target className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{result.metrics.accuracy}%</p>
                  <p className="text-sm text-muted-foreground">Accuracy</p>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-lg">
                  <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{result.metrics.responseTime}s</p>
                  <p className="text-sm text-muted-foreground">Response Time</p>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-lg">
                  <DollarSign className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">${result.metrics.cost}</p>
                  <p className="text-sm text-muted-foreground">Cost</p>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-lg">
                  <Zap className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{result.metrics.qualityScore}/10</p>
                  <p className="text-sm text-muted-foreground">Quality Score</p>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-lg">
                  <BarChart3 className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{result.metrics.tokenCount.total}</p>
                  <p className="text-sm text-muted-foreground">Total Tokens</p>
                </div>
                <div className="text-center p-4 bg-secondary/50 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">
                    {(result.metrics.tokenCount.output / result.metrics.tokenCount.input).toFixed(2)}x
                  </p>
                  <p className="text-sm text-muted-foreground">Output Ratio</p>
                </div>
              </div>
            </div>

            {/* Token Breakdown */}
            <div>
              <h3 className="text-lg font-medium mb-4">Token Usage</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Input Tokens</span>
                  <span className="font-medium">{result.metrics.tokenCount.input}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Output Tokens</span>
                  <span className="font-medium">{result.metrics.tokenCount.output}</span>
                </div>
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="text-sm font-medium">Total Tokens</span>
                  <span className="font-bold">{result.metrics.tokenCount.total}</span>
                </div>
              </div>
            </div>

            {/* Timestamp */}
            <div>
              <h3 className="text-lg font-medium mb-2">Execution Details</h3>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Executed At</span>
                <span className="text-sm">{formatDate(result.timestamp)}</span>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-border flex justify-end space-x-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={() => handleExportResults('json')}>
              <Download className="w-4 h-4 mr-2" />
              Export Details
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Results Visualization</h1>
            <p className="text-muted-foreground mt-1">
              Analyze and compare your evaluation results with interactive charts and comprehensive reports
            </p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={loadResults}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => handleExportResults('csv')}>
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-border">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('results')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'results'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-4 h-4" />
                <span>Results Overview</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'reports'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>Comprehensive Reports</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'comparison'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>Comparative Analysis</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'export'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Export Reports</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 p-4 bg-secondary/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <BarChart3 className="w-4 h-4 text-muted-foreground" />
            <select
              value={selectedEvaluationId || ''}
              onChange={(e) => setSelectedEvaluationId(Number(e.target.value))}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
            >
              <option value="" disabled>
                Select Evaluation
              </option>
              {evaluations.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
            >
              <option value="1d">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
            >
              <option value="accuracy">Accuracy</option>
              <option value="responseTime">Response Time</option>
              <option value="cost">Cost</option>
              <option value="qualityScore">Quality Score</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={selectedModels[0]}
              onChange={(e) => setSelectedModels([e.target.value])}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
            >
              <option value="all">All Models</option>
              {/* Model options could be dynamic in the future */}
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              <option value="gpt-4.1-nano">gpt-4.1-nano</option>
              <option value="gpt-5-nano">gpt-5-nano</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Loading results...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tab Content */}
            {activeTab === 'results' && (
              <div className="space-y-6">
                {/* Performance Over Time */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center space-x-2">
                        <TrendingUp className="w-5 h-5" />
                        <span>Performance Over Time</span>
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleSection('performance')}
                      >
                        {expandedSections.performance ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  {expandedSections.performance && (
                    <CardContent>
                      <div className="h-80">
                        {performanceOverTimeData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={performanceOverTimeData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="date" />
                              <YAxis />
                              <Tooltip
                                content={({ active, payload, label }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                                        <p className="font-medium">{label}</p>
                                        <p className="text-sm text-muted-foreground">
                                          {data.provider} {data.model}
                                        </p>
                                        <p className="text-sm">
                                          {selectedMetric === 'accuracy' && `Accuracy: ${data.accuracy}%`}
                                          {selectedMetric === 'responseTime' && `Response Time: ${data.responseTime}s`}
                                          {selectedMetric === 'cost' && `Cost: $${data.cost}`}
                                          {selectedMetric === 'qualityScore' && `Quality: ${data.qualityScore}/10`}
                                        </p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey={selectedMetric}
                                stroke="#8884d8"
                                strokeWidth={2}
                                dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground">
                            No performance data available
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Model Comparison */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center space-x-2">
                        <ArrowUpDown className="w-5 h-5" />
                        <span>Model Comparison</span>
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleSection('comparison')}
                      >
                        {expandedSections.comparison ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  {expandedSections.comparison && (
                    <CardContent>
                      <div className="h-80">
                        {modelComparisonChartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={modelComparisonChartData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="accuracy" fill="#8884d8" name="Accuracy (%)" />
                              <Bar dataKey="qualityScore" fill="#82ca9d" name="Quality Score" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground">
                            No comparison data available
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Cost vs Performance Analysis */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center space-x-2">
                        <DollarSign className="w-5 h-5" />
                        <span>Cost vs Performance Analysis</span>
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleSection('trends')}
                      >
                        {expandedSections.trends ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  {expandedSections.trends && (
                    <CardContent>
                      <div className="h-80">
                        {costVsPerformanceData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart data={costVsPerformanceData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="cost" name="Cost ($)" />
                              <YAxis dataKey="accuracy" name="Accuracy (%)" />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                                        <p className="font-medium">{data.model}</p>
                                        <p className="text-sm">Cost: ${data.cost}</p>
                                        <p className="text-sm">Accuracy: {data.accuracy}%</p>
                                        <p className="text-sm">Response Time: {data.responseTime}s</p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Scatter dataKey="accuracy" fill="#8884d8" />
                            </ScatterChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground">
                            No trend data available
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Results Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left p-3">Evaluation</th>
                            <th className="text-left p-3">Model</th>
                            <th className="text-left p-3">Accuracy</th>
                            <th className="text-left p-3">Response Time</th>
                            <th className="text-left p-3">Cost</th>
                            <th className="text-left p-3">Quality</th>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.map((result) => (
                            <tr key={result.id} className="border-b border-border hover:bg-accent/50">
                              <td className="p-3">
                                <div>
                                  <p className="font-medium">{result.evaluationName}</p>
                                  <p className="text-sm text-muted-foreground">{result.template}</p>
                                </div>
                              </td>
                              <td className="p-3">
                                <Badge variant="outline">
                                  {result.provider} {result.model}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <span className="font-medium">{result.metrics.accuracy}%</span>
                              </td>
                              <td className="p-3">
                                <span className="font-medium">{result.metrics.responseTime}s</span>
                              </td>
                              <td className="p-3">
                                <span className="font-medium">${result.metrics.cost}</span>
                              </td>
                              <td className="p-3">
                                <span className="font-medium">{result.metrics.qualityScore}/10</span>
                              </td>
                              <td className="p-3">
                                <span className="text-sm">{formatDate(result.timestamp)}</span>
                              </td>
                              <td className="p-3">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedResult(result);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Comprehensive Reports Tab */}
            {activeTab === 'reports' && (
              <div className="space-y-6">
                {selectedEvaluationId ? (
                  <EvaluationReportDashboard evaluationId={selectedEvaluationId} />
                ) : (
                  <Card>
                    <CardContent className="text-center py-12">
                      <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">Select an Evaluation</h3>
                      <p className="text-muted-foreground mb-4">
                        Choose an evaluation to view comprehensive reports with detailed metrics analysis.
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {[1, 2, 3, 4].map(id => (
                          <Button
                            key={id}
                            variant="outline"
                            onClick={() => setSelectedEvaluationId(id)}
                          >
                            Evaluation {id}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Comparative Analysis Tab */}
            {activeTab === 'comparison' && (
              <div className="space-y-6">
                <ComparativeAnalysis reports={reports} />
              </div>
            )}

            {/* Export Tab */}
            {activeTab === 'export' && (
              <div className="space-y-6">
                <ReportExporter
                  report={reports[0]}
                  onExport={(config) => {
                    console.log('Exporting with config:', config);
                    // Handle export logic here
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Detail Modal */}
        <ResultDetailModal
          result={selectedResult}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedResult(null);
          }}
        />
      </div>
    </EvaluationLayout>
  );
};

export default ResultsVisualization;
